import { type ApolloClient } from "@apollo/client";
import {
  type Post,
  type PostTileAsset,
  type ProfileLayoutModelEntry,
} from "@package/ui-gql/index-data-access";
import {
  type PostTileAssetWithPost,
  type ProfileLayoutModelManager,
} from "./wall-manager-profile";
import { getPosts, getPostsWithTagsAndLayout } from "./wall-utils-gql";

export class ProfileLayoutModelEntryManager {
  private assets: PostTileAssetWithPost[] = [];
  private assetIndex: number = 0;

  constructor(
    private readonly client: ApolloClient<object>,
    private readonly layout: string,
    private readonly profileLayoutModelEntry: ProfileLayoutModelEntry,
    private readonly profileLayoutModelManager: ProfileLayoutModelManager,
  ) {}

  public async initialize(): Promise<void> {
    const postIds = (this.profileLayoutModelEntry.posts ?? []).filter(
      (p) => !!p,
    ) as string[];
    const tagIds = (this.profileLayoutModelEntry.tags ?? []).filter(
      (t) => !!t,
    ) as string[];
    const postsTags = await getPostsWithTagsAndLayout(
      this.client,
      tagIds,
      this.layout,
    );
    const posts = await getPosts(this.client, postIds);
    const allPosts = posts.concat(postsTags);
    const relatedPosts: { [key in string]: Post[] } = {};

    await Promise.all(
      allPosts.map(async (post) => {
        if (post?.id && post?.relatedPosts?.length) {
          relatedPosts[post.id] = await getPosts(
            this.client,
            post.relatedPosts.filter((p) => !!p),
          );
        }
      }),
    );

    this.assetIndex = 0;
    this.assets = allPosts
      .filter((p) => !!p?.tileAssets?.length)
      .map((p) =>
        p?.tileAssets
          ?.filter((a) => !!a)
          ?.filter((a) => a.layout === this.layout)
          ?.map((a) => {
            const asset: PostTileAssetWithPost = {
              id: generateTileAssetHash(a, p),
              asset: a,
              post: p,
              relatedPosts: (p.id && relatedPosts[p.id]) || [],
            };

            return asset;
          }),
      )
      .filter((a) => !!a)
      .flat();

    if (this.assets.length > 0) {
      this.profileLayoutModelManager.add(this.assets[0]);
    }
  }

  public getNextTileAsset(): PostTileAssetWithPost | undefined {
    if (this.assets.length === 0) {
      return undefined;
    }

    if (this.assets.length === 1) {
      return this.assets[0];
    }

    let activeAssetCurrent = this.getActiveAsset();
    if (activeAssetCurrent) {
      this.profileLayoutModelManager.delete(activeAssetCurrent);
    }

    const currentIndex = this.assetIndex;
    do {
      if (this.assetIndex >= this.assets.length - 1) {
        this.assetIndex = 0;
      } else {
        this.assetIndex++;
      }

      activeAssetCurrent = this.getActiveAsset();
    } while (
      currentIndex !== this.assetIndex &&
      (!activeAssetCurrent ||
        this.profileLayoutModelManager.has(activeAssetCurrent))
    );

    if (activeAssetCurrent) {
      if (
        this.profileLayoutModelManager.has(activeAssetCurrent) &&
        currentIndex === this.assetIndex
      ) {
        this.assetIndex = Math.floor(Math.random() * this.assets.length);
        activeAssetCurrent = this.getActiveAsset();
      }

      if (activeAssetCurrent) {
        this.profileLayoutModelManager.add(activeAssetCurrent);
      }
    }

    return this.getActiveAsset();
  }

  public getActiveAsset(): PostTileAssetWithPost | undefined {
    if (this.assetIndex < this.assets.length && this.assetIndex >= 0) {
      return this.assets[this.assetIndex];
    }

    return undefined;
  }
}

function generateTileAssetHash(asset: PostTileAsset, post: Post): string {
  try {
    return `${post.id}:${JSON.stringify(asset)}`;
  } catch (e) {
    console.error(
      "[wall-manager-profile-layout]: generateTileAssetHash error: ",
      e,
    );
  }

  return "";
}
